import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { AVAILABLE_TOKENS } from "@/lib/merge-fields";
import { Plus, X, Trash2 } from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";

interface Template {
  id: string;
  name: string;
  subject: string;
  bodyHtml: string;
  category?: string;
  isShared: boolean;
  userId?: string | null;
  user?: { id: string; name: string };
}

const EMPTY: Omit<Template, "id"> = {
  name: "", subject: "", bodyHtml: "", category: "", isShared: false, userId: null,
};

export default function EmailTemplatesPage() {
  const me = useAuthStore((s) => s.me);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Template | null>(null);
  const [draft, setDraft] = useState<Omit<Template, "id">>(EMPTY);
  const [showMerge, setShowMerge] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["/email-templates"],
    queryFn: async () => (await api.get("/email-templates")).data.data as Template[],
  });

  const isAdmin = me?.role === "SUPER_ADMIN" || me?.role === "ADMIN";

  function openNew() {
    setEditing(null);
    setDraft(EMPTY);
    setError(null);
  }

  function openEdit(t: Template) {
    setEditing(t);
    setDraft({ name: t.name, subject: t.subject, bodyHtml: t.bodyHtml, category: t.category ?? "", isShared: t.isShared });
    setError(null);
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        name: draft.name,
        subject: draft.subject,
        bodyHtml: draft.bodyHtml,
        category: draft.category || undefined,
        isShared: draft.isShared,
      };
      if (editing) return (await api.put(`/email-templates/${editing.id}`, payload)).data;
      return (await api.post("/email-templates", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/email-templates"] });
      setEditing(null);
      setDraft(EMPTY);
    },
    onError: (err: any) => setError(err?.response?.data?.message ?? "Save failed."),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/email-templates/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/email-templates"] }),
  });

  function insertToken(token: string) {
    setDraft((d) => ({ ...d, bodyHtml: d.bodyHtml + token }));
    setShowMerge(false);
  }

  return (
    <>
      <PageHeader
        title="Email Templates"
        description="Reusable subject + body with merge fields. Shared templates are visible to everyone; personal ones are only yours."
        actions={<Button onClick={openNew}><Plus className="mr-1 h-4 w-4"/>New Template</Button>}
      />

      <div className="grid gap-4 p-6 md:grid-cols-2">
        <Card>
          <CardContent className="p-0">
            <Table>
              <Thead>
                <Tr><Th>Name</Th><Th>Subject</Th><Th>Scope</Th><Th></Th></Tr>
              </Thead>
              <Tbody>
                {isLoading && <Tr><Td colSpan={4} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
                {!isLoading && (data?.length ?? 0) === 0 && (
                  <Tr><Td colSpan={4} className="py-8 text-center text-muted-foreground">No templates yet.</Td></Tr>
                )}
                {data?.map((t) => (
                  <Tr key={t.id} className="group">
                    <Td>
                      <button onClick={() => openEdit(t)} className="font-medium hover:underline">{t.name}</button>
                      {t.category && <span className="ml-2 text-xs text-muted-foreground">{t.category}</span>}
                    </Td>
                    <Td className="text-muted-foreground truncate max-w-xs">{t.subject}</Td>
                    <Td>
                      {t.isShared
                        ? <Badge variant="secondary">Shared</Badge>
                        : <Badge variant="outline">Mine</Badge>}
                    </Td>
                    <Td>
                      {(t.userId === me?.id || (t.isShared && isAdmin)) && (
                        <button
                          onClick={() => { if (confirm(`Delete "${t.name}"?`)) delMut.mutate(t.id); }}
                          className="opacity-0 group-hover:opacity-100 text-destructive"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4"/>
                        </button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">{editing ? "Edit Template" : "New Template"}</h3>
              {editing && (
                <button onClick={openNew} aria-label="Close" className="text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4"/>
                </button>
              )}
            </div>

            <div>
              <Label htmlFor="tn">Name</Label>
              <Input id="tn" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="tc">Category</Label>
              <Input id="tc" placeholder="Intro / Follow-up / Renewal" value={draft.category ?? ""} onChange={(e) => setDraft({ ...draft, category: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="ts">Subject</Label>
              <Input id="ts" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="tb">Body</Label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowMerge((v) => !v)}
                    className="rounded-md border px-2 py-1 text-xs hover:bg-muted"
                  >
                    Insert merge field
                  </button>
                  {showMerge && (
                    <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-md border bg-popover shadow-lg">
                      <ul className="max-h-64 overflow-y-auto text-sm">
                        {AVAILABLE_TOKENS.map((t) => (
                          <li key={t.token}>
                            <button
                              type="button"
                              onClick={() => insertToken(t.token)}
                              className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-muted"
                            >
                              <span>{t.label}</span>
                              <code className="text-xs text-muted-foreground">{t.token}</code>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <textarea
                id="tb"
                value={draft.bodyHtml}
                onChange={(e) => setDraft({ ...draft, bodyHtml: e.target.value })}
                className="min-h-[200px] w-full rounded-md border bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Hi {{contact.firstName}}, ..."
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                id="sh"
                type="checkbox"
                checked={draft.isShared}
                onChange={(e) => setDraft({ ...draft, isShared: e.target.checked })}
                disabled={!isAdmin}
              />
              <Label htmlFor="sh">
                Share with whole org
                {!isAdmin && <span className="ml-1 text-xs text-muted-foreground">(admin only)</span>}
              </Label>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex justify-end gap-2">
              {editing && <Button variant="outline" onClick={openNew}>Cancel</Button>}
              <Button
                onClick={() => saveMut.mutate()}
                disabled={!draft.name || !draft.subject || !draft.bodyHtml || saveMut.isPending}
              >
                {saveMut.isPending ? "Saving…" : editing ? "Update" : "Create"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
